import { Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FieldValues,
    UseFieldArrayRemove,
    UseFieldArrayUpdate,
    useFormContext,
    useWatch,
} from 'react-hook-form';

import { useLocation } from 'react-router-dom';
import { selectBookerObject } from '../../../../../../app/Slices/BookerSlice';
import { CustomButton } from '../../../../../../layout/styledcomponents/CustomButton';
import CustomInputLabel from '../../../../../../layout/styledcomponents/CustomInputLabel';
import UserRole from '../../../../../../shared/AuthRoles';
import FormCheckbox from '../../../../../shared/FormCheckbox';
import FormSelect from '../../../../../shared/FormSelect';
import FormTextField from '../../../../../shared/FormTextField';

export interface Driver {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    licenceNo: string;
    licenceType: string;
}

interface Props {
    existingDriver: Driver;
    index: any;
    update: UseFieldArrayUpdate<FieldValues, 'bookingInfo.drivers'>;
    remove: UseFieldArrayRemove;
}

const DriverForm = ({ existingDriver, index, update, remove }: Props) => {
    const { control } = useFormContext();

    const drivers = useWatch({ name: 'bookingInfo.drivers', control });

    const driverSameAsBooker = useWatch({
        name: 'bookingInfo.driverSameAsBooker',
        control,
    });
    const bookerIsSelected = useWatch({ name: 'booker', control });

    const bookerObject = useSelector(selectBookerObject);
    const loggedInUser = UserRole();
    const location = useLocation();

    useEffect(() => {
        if (
            Object.keys(bookerObject).length > 0 &&
            driverSameAsBooker &&
            drivers[0].email !== bookerObject.email
        ) {
            const mainDriver =
                location?.state?.reservationData?.bookingInfo?.drivers[0];

            const driverData = {
                firstName: bookerObject.firstName,
                lastName: bookerObject.lastName,
                phone: bookerObject.phone,
                email: bookerObject.email,
                licenceNo:
                    location?.state?.page === 'aiGuestReservation'
                        ? mainDriver.licenceNo
                        : location?.state?.name === 'editReservation'
                          ? drivers[0].licenceNo
                          : '',
                licenceType:
                    location?.state?.page === 'aiGuestReservation'
                        ? mainDriver.licenceType
                        : location?.state?.name === 'editReservation'
                          ? drivers[0].licenceType
                          : '',
            };

            update(0, driverData);
        }
    }, [bookerObject]);

    const handleDeleteDriver = () => {
        remove(index);
    };

    const handleCheck = () => {
        if (driverSameAsBooker) {
            update(0, {
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                licenceNo: '',
                licenceType: '',
            });
        } else {
            update(0, {
                firstName: bookerObject.firstName,
                lastName: bookerObject.lastName,
                phone: bookerObject.phone,
                email: bookerObject.email,
                licenceNo: '',
                licenceType: '',
            });
        }
    };

    return (
        <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
            <Grid>
                {index === 0 && !loggedInUser.role.includes('owner') && (
                    <Grid
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            paddingBottom: '10px',
                        }}
                    >
                        <Grid
                            sx={{
                                width: '100%',
                                display: 'flex',
                                gap: '10px',
                                paddingBottom: '5px',
                                alignItems: 'center',
                            }}
                        >
                            <Grid>
                                <FormCheckbox
                                    id='driver__checkbox'
                                    name='bookingInfo.driverSameAsBooker'
                                    // defaultChecked={false}
                                    control={control}
                                    disabled={!bookerIsSelected}
                                    onClick={handleCheck}
                                />
                            </Grid>
                            <Grid>
                                <Typography
                                    component='label'
                                    htmlFor='driver__checkbox'
                                    variant='body3'
                                    sx={{ lineHeight: 1.35 }}
                                >
                                    Driver is the same person as who the
                                    reservation is for
                                </Typography>
                                {!bookerIsSelected && (
                                    <Typography
                                        color='red'
                                        sx={{
                                            fontSize: '11px',
                                        }}
                                    >
                                        (Please select who the booking is for)
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {drivers?.length > 1 && (
                    <Typography
                        sx={{
                            pb: '10px',
                        }}
                    >
                        {index === 0
                            ? `   Main Driver:`
                            : `Additional Driver ${index}:`}
                    </Typography>
                )}

                <Grid sx={{ paddingBottom: '15px' }}>
                    <CustomInputLabel
                        required={!existingDriver?.firstName}
                        htmlFor={`driver-first-name__input_index_${index}`}
                    >
                        First Name
                    </CustomInputLabel>
                    <FormTextField
                        id={`driver-first-name__input_index_${index}`}
                        name={`bookingInfo.drivers[${index}].firstName`}
                        control={control}
                        disabled={
                            driverSameAsBooker &&
                            index === 0 &&
                            !loggedInUser.role.includes('owner')
                        }
                    />
                </Grid>
                <Grid pb='15px'>
                    <CustomInputLabel
                        required={!existingDriver?.lastName}
                        htmlFor={`driver-last-name__input_index_${index}`}
                    >
                        Last Name
                    </CustomInputLabel>
                    <FormTextField
                        id={`driver-last-name__input_index_${index}`}
                        name={`bookingInfo.drivers[${index}].lastName`}
                        control={control}
                        disabled={
                            driverSameAsBooker &&
                            index === 0 &&
                            !loggedInUser.role.includes('owner')
                        }
                    />
                </Grid>
                <Grid pb='15px'>
                    <CustomInputLabel
                        required={!existingDriver?.phone}
                        htmlFor={`driver-phone__input_index_${index}`}
                    >
                        Phone
                    </CustomInputLabel>
                    <FormTextField
                        id={`driver-phone__input_index_${index}`}
                        name={`bookingInfo.drivers[${index}].phone`}
                        control={control}
                        disabled={
                            driverSameAsBooker &&
                            index === 0 &&
                            !loggedInUser.role.includes('owner')
                        }
                    />
                </Grid>
                <Grid pb='15px'>
                    <CustomInputLabel
                        required={!existingDriver?.email}
                        htmlFor={`driver-email__input_index_${index}`}
                    >
                        Email
                    </CustomInputLabel>
                    <FormTextField
                        id={`driver-email__input_index_${index}`}
                        name={`bookingInfo.drivers[${index}].email`}
                        control={control}
                        disabled={
                            driverSameAsBooker &&
                            index === 0 &&
                            !loggedInUser.role.includes('owner')
                        }
                    />
                </Grid>
                <Grid pb='15px'>
                    <CustomInputLabel
                        required={!existingDriver?.licenceNo}
                        htmlFor={`driver-licence-no__input_index_${index}`}
                    >
                        Licence No.
                    </CustomInputLabel>
                    <FormTextField
                        id={`driver-licence-no__input_index_${index}`}
                        name={`bookingInfo.drivers[${index}].licenceNo`}
                        control={control}
                    />
                </Grid>
                <Grid pb='15px'>
                    <CustomInputLabel
                        required={!existingDriver?.licenceType}
                        id={`driver-license-type__label_index_${index}`}
                        htmlFor={`driver-licence-type__input_index_${index}`}
                    >
                        Licence Type
                    </CustomInputLabel>
                    <FormSelect
                        id={`driver-licence-type__input_index_${index}`}
                        labelId={`driver-license-type__label_index_${index}`}
                        name={`bookingInfo.drivers[${index}].licenceType`}
                        control={control}
                        options={[
                            { value: 'full', label: 'Full' },
                            { value: 'p2', label: 'P2' },
                            { value: 'p1', label: 'P1' },
                        ]}
                    />
                </Grid>
            </Grid>
            {index > 0 && (
                <Grid
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <CustomButton
                        variant='contained'
                        color='warning'
                        onClick={handleDeleteDriver}
                    >
                        Delete
                    </CustomButton>
                </Grid>
            )}
        </Grid>
    );
};

export default DriverForm;
